/**
 * Contact-field capture model (R10 — S1 welcome card, voice-fill live echo).
 *
 * The welcome card captures name + company only (design doc §4.1). Three fill modes feed
 * the same field map (§4.2): business-card scan, voice-fill live echo, and typing.
 *
 * Voice-fill echoes arrive as `ui.contact_field {field, value, source, status}` over RTVI
 * (voice-capture-contract.md, R26). A field is *draft* while Gigi/STT is still resolving it,
 * and *final* once confirmed. The card renders draft values with a distinct treatment
 * (dashed underline + "hearing…" affordance) until final.
 *
 * Typing always wins: once the visitor edits a field by hand it becomes a typed final and a
 * later voice draft for the same field must NOT clobber it (a wrong-STT loop is worse than a
 * keyboard — §4.2). This module encodes that precedence so it's unit-testable.
 */

/**
 * The fields the contact cards capture. The S1 welcome card uses name + company only (§4.1);
 * the S7 close card adds an optional phone (a second contact point for the lead — R13 U4). Email
 * is captured on the close card by keyboard/scan only (never voice — §4.2).
 */
export type ContactFieldName = 'name' | 'company' | 'phone';

export type ContactFieldSource = 'voice' | 'card' | 'typed';
export type ContactFieldStatus = 'draft' | 'final';

export type ContactFieldState = {
  value: string;
  source: ContactFieldSource;
  status: ContactFieldStatus;
};

/** field → its current capture state. Absent key = not yet captured. */
export type ContactFieldMap = Partial<Record<ContactFieldName, ContactFieldState>>;

/**
 * The welcome-card (S1) capture scope — name + company only (§4.1). buildContactSubmission /
 * hasAnyContact / applyCardScan iterate THIS, so the welcome card never tries to submit phone.
 */
export const CONTACT_FIELDS: readonly ContactFieldName[] = ['name', 'company'];

/** Every field a `ui.contact_field` echo may carry (adds phone for the S7 close card — R13 U4). */
export const ALL_CONTACT_FIELDS: readonly ContactFieldName[] = ['name', 'company', 'phone'];

export function isContactFieldName(v: unknown): v is ContactFieldName {
  return v === 'name' || v === 'company' || v === 'phone';
}

/**
 * Merge an incoming `ui.contact_field` echo (voice or card) into the field map.
 *
 * Precedence rules:
 *   - A typed final is authoritative: an incoming *voice* echo (draft OR final) never
 *     overwrites a field the visitor typed. (Card scan may still overwrite a typed value
 *     only if the visitor hasn't finalised by typing — but we keep it conservative: any
 *     `typed` final blocks voice; card overwrites non-typed only.)
 *   - Otherwise the incoming echo replaces the field (last-writer-wins for voice/card).
 */
export function mergeContactField(
  map: ContactFieldMap,
  field: ContactFieldName,
  value: string,
  source: Exclude<ContactFieldSource, 'typed'>,
  status: ContactFieldStatus
): ContactFieldMap {
  const existing = map[field];
  // A hand-typed final is sacred — never let a voice/card echo clobber it.
  if (existing?.source === 'typed' && existing.status === 'final') {
    return map;
  }
  return { ...map, [field]: { value, source, status } };
}

/** Apply a hand-typed edit — always a typed final, always authoritative. */
export function applyTypedField(
  map: ContactFieldMap,
  field: ContactFieldName,
  value: string
): ContactFieldMap {
  return { ...map, [field]: { value, source: 'typed', status: 'final' } };
}

/**
 * Apply a full card-scan result: fields fill live, marked source:'card', status:'final'.
 * Iterates ALL_CONTACT_FIELDS so a close-card scan can also fill phone (R13 U4); the welcome card
 * only passes name/company so nothing extra leaks there.
 */
export function applyCardScan(
  map: ContactFieldMap,
  fields: Partial<Record<ContactFieldName, string>>
): ContactFieldMap {
  let next = map;
  for (const f of ALL_CONTACT_FIELDS) {
    const v = fields[f];
    if (typeof v === 'string' && v.trim() !== '') {
      next = { ...next, [f]: { value: v.trim(), source: 'card', status: 'final' } };
    }
  }
  return next;
}

/** Build the `contact-submitted` payload — only non-empty fields, with the dominant method. */
export function buildContactSubmission(map: ContactFieldMap): {
  fields: Partial<Record<ContactFieldName, string>>;
  method: 'card' | 'voice' | 'typed';
} {
  const fields: Partial<Record<ContactFieldName, string>> = {};
  const sources = new Set<ContactFieldSource>();
  for (const f of CONTACT_FIELDS) {
    const st = map[f];
    if (st && st.value.trim() !== '') {
      fields[f] = st.value.trim();
      sources.add(st.source);
    }
  }
  // Method precedence for the lead record: card (hero path) > typed > voice.
  const method: 'card' | 'voice' | 'typed' = sources.has('card')
    ? 'card'
    : sources.has('typed')
      ? 'typed'
      : 'voice';
  return { fields, method };
}

/** True if there's at least one non-empty captured field (enables Continue). */
export function hasAnyContact(map: ContactFieldMap): boolean {
  return CONTACT_FIELDS.some((f) => (map[f]?.value.trim() ?? '') !== '');
}

/**
 * R16 V2 — build the S7 close-card `submit-contact` payload from the visitor-entered fields.
 *
 * Field evidence (dev-77, 2026-07-09): the visitor typed a PHONE on the close card but the lead
 * persisted `phone: None`. This helper pins the CLIENT half: PHONE (and every present field) is
 * carried into the payload verbatim, trimmed; empty/whitespace-only fields are dropped (hard rule:
 * never fabricate or send an empty string). The email is always required by the form before this is
 * called, so it's carried when present. Pure so the exact wire shape is unit-testable without a WS.
 */
export function buildContactFormPayload(fields: {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  country?: string;
}): Partial<Record<'name' | 'email' | 'phone' | 'company' | 'country', string>> {
  const out: Partial<Record<'name' | 'email' | 'phone' | 'company' | 'country', string>> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (typeof v === 'string' && v.trim() !== '') {
      out[k as keyof typeof out] = v.trim();
    }
  }
  return out;
}

/**
 * A parsed `ui.contact_field` echo, normalised to the store's echo shape.
 * `source` is 'card' when the server marks it a card scan, else 'voice' (email is never voice).
 * `status` is 'final' only on an explicit final, else 'draft'.
 */
export type ParsedContactField = {
  field: ContactFieldName;
  value: string;
  source: 'voice' | 'card';
  status: ContactFieldStatus;
};

/**
 * R13 U6 — tolerantly parse a `ui.contact_field` message into a normalised echo, or null if it
 * isn't a usable contact-field echo. This is the fix for the F4 REGRESSION (the welcome card stayed
 * blank during voice capture): the client previously read `field/value/source/status` ONLY from the
 * message top level, so if the server nests them under `payload` (the exact seam-class bug that hit
 * `ui.categories` — emission shape ≠ subscription shape) every echo silently dropped.
 *
 * Accepts BOTH nesting variants and merges them (top-level wins, `payload.*` is the fallback):
 *   a) top-level:  { type:'ui.contact_field', field, value, source?, status? }
 *   b) nested:     { type:'ui.contact_field', payload:{ field, value, source?, status? } }
 *
 * The caller passes the whole message; this fires per-field (name can arrive before company — each
 * lands independently). Unknown fields / missing value → null (the caller ignores it).
 */
export function parseContactFieldEcho(msg: unknown): ParsedContactField | null {
  if (!msg || typeof msg !== 'object') return null;
  type Raw = {
    field?: unknown;
    value?: unknown;
    source?: unknown;
    status?: unknown;
  };
  const outer = msg as Raw & { payload?: unknown };
  const nested =
    outer.payload && typeof outer.payload === 'object' ? (outer.payload as Raw) : ({} as Raw);
  const field = outer.field ?? nested.field;
  const value = outer.value ?? nested.value;
  if (!isContactFieldName(field) || typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  const rawSource = outer.source ?? nested.source;
  const rawStatus = outer.status ?? nested.status;
  return {
    field,
    value,
    // Email is never voice (§4.2); for name/company/phone the server may say 'card' on a scan.
    source: rawSource === 'card' ? 'card' : 'voice',
    status: rawStatus === 'final' ? 'final' : 'draft',
  };
}
